import "./styles.css";
import { MATCH_PHASES, PLAYER_COLORS } from "@flood-watch/shared";
import { GameAudio } from "./audio.js";
import { Controls } from "./controls.js";
import { fromServerPosition, toServerSnapshot, toThreeCourse } from "./course-adapter.js";
import { GameNetwork } from "./network.js";
import { PlayerController } from "./player.js";
import { colorTakenMessage, GameUI } from "./ui.js";
import { World } from "./world.js";
import { animateRunner, paintRacer } from "./racer.js";

const canvas = document.querySelector("#game-canvas");
const world = new World(canvas);
const controls = new Controls(canvas);
const audio = new GameAudio();
const ui = new GameUI();

const storedColor = PLAYER_COLORS.find((color) => color.id === localStorage.getItem("flood-watch-color"));
let selectedColor = storedColor ?? PLAYER_COLORS[0];
let localId = null;
let localMesh = world.createPlayer(selectedColor.hex, true);
let courseData = null;
let course = [];
let phase = "menu";
let previousPhase = "menu";
let latestState = null;
let sequence = 0;
let elapsed = 0;
let waterHeight = -5;
let lastFrame = performance.now();
let remotePositions = new Map();
let menuCameraAngle = 0;
let wasEliminated = false;

const player = new PlayerController(localMesh, world, {
  onJump: () => audio.jump(),
  onBounce: () => {
    audio.bounce();
    ui.toast("BOING!");
  },
  onCheckpoint: (_platformIndex, platformId) => {
    const checkpointIndex = courseData.checkpoints.findIndex((checkpoint) => checkpoint.platformId === platformId);
    if (checkpointIndex >= 0) network.checkpoint(checkpointIndex);
    audio.checkpoint();
    ui.toast("SAFE DECK REACHED");
  },
  onPickup: (platformId) => {
    network.pickup(platformId);
    audio.pickup();
    ui.toast("TIDE FROZEN FOR 3 SECONDS!");
  },
  onEliminated: () => {
    audio.splash();
    ui.toast("SPLASH! Respawning…", 2200);
    network.sendSnapshot(toServerSnapshot(player.snapshot(), player.velocity, sequence++), true);
  },
  onRespawn: () => ui.toast("Back in the race!"),
  onFinish: () => {
    audio.finish();
    network.finish();
    ui.toast("RESCUE BEACON REACHED!", 2400);
  },
});

const network = new GameNetwork({
  onConnection(online, detail) {
    if (online) localId = network.socket.id;
    ui.connection(online, detail);
  },
  onJoined(payload) {
    localId = payload.playerId;
    if (payload.color) paintRacer(localMesh, payload.color);
    if (payload.colorTaken) ui.toast(colorTakenMessage(payload) ?? "That color was taken. This one is yours.", 4200);
  },
  onCourse(payload) {
    courseData = payload.course;
    course = toThreeCourse(courseData);
    world.loadCourse(course);
    remotePositions.clear();
    player.spawn(0);
    player.active = false;
  },
  onRoomState(state) {
    receiveState(state);
  },
  onMatchState(state) {
    receiveState(state);
  },
  onRemoteSnapshot(snapshot) {
    if (!course.length) return;
    remotePositions.set(snapshot.playerId, fromServerPosition(snapshot, course));
    syncRemotePlayers();
  },
  onPlayerLeft(payload) {
    remotePositions.delete(payload.playerId);
    syncRemotePlayers();
  },
  onError(error) {
    if (["rate-limited", "stale-sequence", "death-cooldown", "death-not-valid"].includes(error.reason)) return;
    if (error.reason === "color-taken") {
      ui.showPanel("menu");
      ui.toast("Every color is taken. Try another room.");
      return;
    }
    console.warn("Server rejected an action", error);
    ui.toast(humanizeError(error.reason ?? "Network error"));
  },
});

function receiveState(state) {
  latestState = colorizeState(state);
  previousPhase = phase;
  phase = state.phase;
  waterHeight = (state.waterHeight ?? 0) / 50;

  if (state.phase === MATCH_PHASES.COUNTDOWN) {
    const remaining = state.remainingMs ?? Math.max(0, state.phaseEndsAt - state.serverTime);
    if (course.length && remaining <= 3300) {
      ui.showGame();
      player.active = false;
      player.mesh.visible = true;
      ui.raceCountdown(remaining / 1000, audio);
    } else {
      ui.lobby({ ...latestState, countdown: remaining }, localId);
    }
  }

  if (state.phase === MATCH_PHASES.ACTIVE) {
    if (previousPhase !== MATCH_PHASES.ACTIVE) {
      player.spawn(0);
      sequence = 0;
      ui.lastCountdown = null;
      ui.raceCountdown(0, audio);
    }
    const me = state.players.find((entry) => entry.id === localId);
    player.active = !player.finished && !me?.eliminated && me?.placement == null;
    if (me?.eliminated) {
      player.mesh.visible = false;
      if (!wasEliminated) ui.toast("The flood passed your safe deck!", 2600);
    }
    wasEliminated = Boolean(me?.eliminated);
    ui.showGame();
  }

  if (state.respawn?.playerId === localId && state.phase === MATCH_PHASES.ACTIVE) {
    const me = state.players.find((entry) => entry.id === localId);
    const checkpoint = courseData.checkpoints[me?.checkpoint];
    const platformIndex = checkpoint
      ? course.findIndex((platform) => platform.id === checkpoint.platformId)
      : 0;
    player.spawn(Math.max(0, platformIndex));
    player.active = true;
    wasEliminated = false;
    ui.toast("Back at your safe deck!");
  }

  if (state.phase === MATCH_PHASES.FINISHED && previousPhase !== MATCH_PHASES.FINISHED) {
    player.active = false;
    document.querySelector("#rematch-button").disabled = false;
    ui.results(resultState(latestState), localId);
  }

  const me = latestState.players.find((entry) => entry.id === localId);
  if (me?.color) paintRacer(localMesh, me.color);
  syncRemotePlayers();
}

function colorizeState(state) {
  return {
    ...state,
    players: (state.players ?? []).map((entry, index) => ({
      ...entry,
      color: /^#[0-9a-f]{6}$/i.test(entry.color ?? "")
        ? entry.color
        : PLAYER_COLORS[index % PLAYER_COLORS.length].hex,
    })),
  };
}

function resultState(state) {
  const unfinished = state.players
    .filter((entry) => entry.placement == null)
    .sort((a, b) => b.checkpoint - a.checkpoint);
  let fallbackPlace = state.players.filter((entry) => entry.placement != null).length + 1;
  return {
    ...state,
    players: state.players.map((entry) => ({
      ...entry,
      place: entry.placement ?? fallbackPlace + unfinished.indexOf(entry),
      eliminated: entry.placement == null,
    })),
  };
}

function syncRemotePlayers() {
  if (!latestState) return;
  const players = latestState.players.map((entry) => ({
    ...entry,
    position: remotePositions.get(entry.id),
    eliminated: entry.eliminated,
    finished: entry.placement != null,
  }));
  world.setRemotePlayers(players, localId);
}

function updateHud() {
  if (!latestState) return;
  const me = latestState.players.find((entry) => entry.id === localId);
  const ordered = [...latestState.players].sort((a, b) => {
    if (a.placement != null) return -1;
    if (b.placement != null) return 1;
    return (b.checkpoint ?? -1) - (a.checkpoint ?? -1);
  });
  const place = Math.max(1, ordered.findIndex((entry) => entry.id === localId) + 1);
  ui.updateHud({
    place,
    total: latestState.players.length,
    checkpoint: (me?.checkpoint ?? -1) + 1,
    checkpointTotal: courseData?.checkpoints.length ?? 4,
    waterDistance: player.position.y - waterHeight,
    waterPaused: (latestState.waterPausedUntil ?? 0) > (latestState.serverTime ?? 0),
  });
}

function loop(now) {
  requestAnimationFrame(loop);
  const delta = Math.min(0.05, (now - lastFrame) / 1000);
  lastFrame = now;
  elapsed += delta;

  const input = controls.sample();
  if (phase === MATCH_PHASES.ACTIVE) {
    player.update(input, delta, elapsed, waterHeight);
    if (!player.eliminated) {
      network.sendSnapshot(toServerSnapshot(player.snapshot(), player.velocity, sequence++));
    }
    world.updateCamera(player.position, player.cameraYaw, delta);
    updateHud();
  } else if (phase === MATCH_PHASES.COUNTDOWN && course.length) {
    player.update({ x: 0, z: 0, jump: false, look: input.look }, delta, elapsed, waterHeight);
    world.updateCamera(player.position, player.cameraYaw, delta);
  } else {
    menuCameraAngle += delta * 0.35;
    player.mesh.visible = true;
    player.mesh.position.set(-3.2, 0, 0.15);
    player.mesh.rotation.y = 0.35;
    animateRunner(player.mesh, elapsed, 0.12, true);
    world.camera.position.set(-0.35 + Math.sin(menuCameraAngle) * 0.18, 1.5, 5.2);
    world.camera.lookAt(-1.85, 1.0, 0);
  }
  world.update(elapsed, delta, waterHeight);
  world.render();
}

document.querySelector("#play-button").addEventListener("click", () => {
  audio.unlock();
  const input = document.querySelector("#name-input");
  const name = input.value.trim().slice(0, 16) || "Rookie";
  input.value = name;
  localStorage.setItem("flood-watch-name", name);
  localStorage.setItem("flood-watch-color", selectedColor.id);
  ui.showPanel("lobby");
  network.join(name, selectedColor.id);
});

document.querySelector("#leave-button").addEventListener("click", () => {
  network.leave();
  phase = "menu";
  latestState = null;
  paintRacer(localMesh, selectedColor.hex);
  ui.showPanel("menu");
});

document.querySelector("#rematch-button").addEventListener("click", () => {
  audio.unlock();
  network.rematch();
  ui.toast("Rematch vote sent");
  document.querySelector("#rematch-button").disabled = true;
});

document.querySelector("#menu-button").addEventListener("click", () => {
  network.leave();
  phase = "menu";
  latestState = null;
  paintRacer(localMesh, selectedColor.hex);
  ui.showPanel("menu");
});

document.querySelector("#mute-button").addEventListener("click", (event) => {
  const muted = audio.toggle();
  event.currentTarget.textContent = muted ? "×" : "♪";
  event.currentTarget.setAttribute("aria-label", muted ? "Unmute sounds" : "Mute sounds");
});

const storedName = localStorage.getItem("flood-watch-name");
if (storedName) document.querySelector("#name-input").value = storedName;

const colorChoices = document.querySelector("#color-choices");
const colorPicked = document.querySelector("#color-picked");
for (const color of PLAYER_COLORS) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "color-choice";
  button.dataset.color = color.id;
  button.style.setProperty("--swatch", color.hex);
  button.setAttribute("role", "radio");
  button.setAttribute("aria-label", color.label);
  button.addEventListener("click", () => selectColor(color.id));
  colorChoices.append(button);
}
selectColor(selectedColor.id);

function selectColor(id) {
  selectedColor = PLAYER_COLORS.find((color) => color.id === id) ?? PLAYER_COLORS[0];
  colorPicked.textContent = selectedColor.label;
  for (const button of colorChoices.querySelectorAll(".color-choice")) {
    button.setAttribute("aria-checked", button.dataset.color === selectedColor.id ? "true" : "false");
  }
  paintRacer(localMesh, selectedColor.hex);
}
document.querySelector("#mute-button").textContent = audio.muted ? "×" : "♪";
requestAnimationFrame(loop);

function humanizeError(reason) {
  return String(reason)
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
